import React, { useEffect, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Row,
  Table,
  Col,
  Card,
  CardBody,
  Badge,
} from "reactstrap"
import { useLocation, useNavigate } from "react-router"
import DeleteModal from "components/Common/DeleteModal"
import Paginations from "components/Common/Paginations"
import Filters from "components/Common/Filters"
import { useSelector, useDispatch } from "react-redux"
import { getAllUsers, restoreAdminUser } from "store/actions/usersAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { showAlert } from "store/reducers/alertReducer"
import { commonItemPerPage, pageSizes } from "components/Common/constants"
import TableShimmer from "components/Shared/TableShimmer"
import TableActionsDropdown from "components/Shared/TableActionsDropdown"
import { useTranslation } from "react-i18next"
import LtrText from "components/Shared/LtrText"
import { ROUTES } from "constants/routes"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"

const BASE_PATH = "/superadmin/users"
const DELETED_STATUS = 3

const DeletedUsers = () => {
  const { t, i18n: { language } } = useTranslation()
  document.title = t("Deleted Users | Super Admin")

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [itemIdToRestore, setItemIdToRestore] = useState(null)
  const [showRestoreModal, setShowRestoreModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [selectedFilters, setSelectedFilters] = useState({
    sortOrder: parseInt(searchParams.get("sortOrder") || "1", 10),
  })
  const [itemsPerPage, setItemsPerPage] = useState(commonItemPerPage)

  const {
    loading,
    isUpdating,
    totalCount,
    totalPages,
    currentPage,
    usersList,
    usersListStatus,
  } = useSelector(state => state.Users)

  const isDeletedUsersListReady =
    !loading && usersListStatus === DELETED_STATUS

  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"

  const [canViewDeletedUsers, setCanViewDeletedUsers] = useState(false)

  useEffect(() => {
    if (!isSuperAdminSession) {
      navigateToAccessibleRoute(navigate)
      return
    }
    const allowed = isPermissionEnabled(
      userRole,
      103,
      "sa_users_5",
      accountType,
      accountScope
    )
    if (!allowed) {
      navigateToAccessibleRoute(navigate)
      return
    }
    setCanViewDeletedUsers(allowed)
  }, [userRole, accountType, accountScope, isSuperAdminSession, navigate])

  const fetchDeletedUsers = (page = pageNumber) => {
    const { sortOrder } = selectedFilters
    dispatch(
      getAllUsers({
        perPage: itemsPerPage,
        pageNo: page,
        status: DELETED_STATUS,
        orderBy: Number(sortOrder),
        keyword: searchTerm || "",
        location: [],
        role: [],
      })
    )
  }

  useEffect(() => {
    if (!canViewDeletedUsers) {
      return
    }
    fetchDeletedUsers(pageNumber)
  }, [canViewDeletedUsers, pageNumber, itemsPerPage])

  const handleFetchUsers = (page = 1) => {
    const { sortOrder } = selectedFilters
    const params = new URLSearchParams()
    if (searchTerm) {
      params.set("search", searchTerm)
    }
    params.set("sortOrder", sortOrder.toString())
    params.set("page", page.toString())

    dispatch(
      getAllUsers({
        perPage: itemsPerPage,
        pageNo: page,
        status: DELETED_STATUS,
        orderBy: Number(sortOrder),
        keyword: searchTerm || "",
        location: [],
        role: [],
      })
    ).then(action => {
      const { statusCode, status } = action?.payload || {}
      if (statusCode === 200 && status) {
        navigate(`${BASE_PATH}/deleted-users?${params.toString()}`)
      }
    })
  }

  const handleSearch = () => handleFetchUsers(1)
  const onPageChange = page => handleFetchUsers(page)
  const handleSearchChange = e => setSearchTerm(e.target.value)
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prev => ({ ...prev, [filterName]: selectedValue }))
  }

  const handleReset = () => {
    setSelectedFilters({ sortOrder: 1 })
    setSearchTerm("")
    setItemsPerPage(commonItemPerPage)
    navigate(`${BASE_PATH}/deleted-users?page=1`)
    dispatch(
      getAllUsers({
        perPage: commonItemPerPage,
        pageNo: 1,
        status: DELETED_STATUS,
        orderBy: 1,
        keyword: "",
        location: [],
        role: [],
      })
    )
  }

  const handleRestore = () => {
    dispatch(restoreAdminUser({ id: itemIdToRestore, status: "1" })).then(
      action => {
        const { statusCode, status, msg } = action?.payload || {}
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowRestoreModal(false)
          setItemIdToRestore(null)
          fetchDeletedUsers(pageNumber)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      }
    )
  }

  const filters = [
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
  ]

  const getDeletedUserActionItems = item => [
    {
      key: "view",
      content: (
        <>
          <i className="mdi mdi-eye font-size-16 text-primary me-1" />
          {t("View")}
        </>
      ),
      onClick: () =>
        navigate(`${BASE_PATH}/deleted-users/user-details?id=${item._id}`),
    },
    {
      key: "restore",
      content: (
        <>
          <i className="mdi mdi-restore font-size-16 text-warning me-1" />
          {t("Restore")}
        </>
      ),
      onClick: () => {
        setItemIdToRestore(item._id)
        setShowRestoreModal(true)
      },
    },
  ]

  return (
    <>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs />
          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <h4 className="card-title mb-3">{t("Deleted Users List")}</h4>
                  <Filters
                    filters={filters}
                    searchTerm={searchTerm}
                    handleSearchChange={handleSearchChange}
                    handleFilterChange={handleFilterChange}
                    handleSearch={handleSearch}
                    onReset={handleReset}
                    selectedFilters={selectedFilters}
                    isCustomPageSize
                    pageSizes={pageSizes}
                    itemsPerPage={itemsPerPage}
                    setItemsPerPage={setItemsPerPage}
                    searchBy={t("Name, Email, Phone Number")}
                    colSize={4}
                    showOnlySearchAndSort
                  />

                  <div>
                    {!isDeletedUsersListReady ? (
                      <TableShimmer row={7} col={6} />
                    ) : usersList.length === 0 ? (
                      <p className="text-center my-2">{t("No users found")}</p>
                    ) : (
                      <div className="table-responsive">
                        <Table className="table mb-0" hover>
                          <thead className="table-light full_th">
                            <tr>
                              <th>{t("S. No.")}</th>
                              <th>{t("Name")}</th>
                              <th>{t("Email")}</th>
                              <th>{t("Phone Number")}</th>
                              <th>{t("Role")}</th>
                              <th>{t("Status")}</th>
                              <th>{t("Action")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {usersList.map((item, index) => (
                              <tr key={item._id}>
                                <th scope="row">
                                  {(currentPage - 1) * itemsPerPage + index + 1}
                                </th>
                                <td>
                                  {item.firstName} {item.lastName}
                                </td>
                                <td>{item.email || "-"}</td>
                                <td>
                                  {item.phoneNumber ? (
                                    <LtrText>{item.phoneNumber}</LtrText>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td>
                                  {language === "sa"
                                    ? item?.role?.nameAr || item?.role?.name || "-"
                                    : item?.role?.name || "-"}
                                </td>
                                <td>
                                  <Badge className="bg-danger">{t("Deleted")}</Badge>
                                </td>
                                <td>
                                  <TableActionsDropdown
                                    items={getDeletedUserActionItems(item)}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </div>

                  {isDeletedUsersListReady && usersList.length > 0 && (
                    <div className="mt-3">
                      <Paginations
                        data={usersList}
                        itemsPerPage={itemsPerPage}
                        onPageChange={onPageChange}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <DeleteModal
        show={showRestoreModal}
        isLoading={isUpdating}
        onDeleteClick={handleRestore}
        onCloseClick={() => {
          setShowRestoreModal(false)
          setItemIdToRestore(null)
        }}
        buttonText={t("Restore")}
        bodyTitle={t("Are you sure you want to restore this account?")}
        icon="mdi mdi-reload"
        btnClass="btn btn-primary"
      />
    </>
  )
}

export default DeletedUsers
